import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { JsonConvert } from 'json2typescript';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { RoutesPath } from '@features/termination/enums';
import { CustomerInformationResponse, ProductListResponse } from '@features/termination/interfaces';
import { Customer, Product, Subscription } from '@features/termination/models';
import {
  CustomerInformationService,
  EmailService,
  ProductListService,
} from '@features/termination/services';
import { TerminationRequestService } from '@features/termination/services/request.service';

import * as fromStore from '../../store';
import * as fromActions from '../actions';

@Injectable()
export class TerminationEffects {
  private readonly store: Store = inject(Store);

  private readonly jsonConvert: JsonConvert = inject(JsonConvert);

  private readonly router: Router = inject(Router);

  private readonly actions$: Actions = inject(Actions);

  private readonly customerInformationService: CustomerInformationService = inject(
    CustomerInformationService,
  );

  private readonly emailService: EmailService = inject(EmailService);

  private readonly requestService: TerminationRequestService = inject(TerminationRequestService);

  private readonly productListService: ProductListService = inject(ProductListService);

  public loadCustomerInformation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationLoad),
      switchMap(() =>
        this.customerInformationService.getCustomerInformation().pipe(
          map((customerInformationResponse: CustomerInformationResponse) => {
            if (customerInformationResponse?.data) {
              return fromActions.CustomerInformationLoadComplete({
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                customer: this.jsonConvert.deserializeObject(
                  customerInformationResponse?.data?.client,
                  Customer,
                ),
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                subscription: this.jsonConvert.deserializeObject(
                  customerInformationResponse?.data?.abonnement,
                  Subscription,
                ),
              });
            } else {
              return fromActions.CustomerInformationLoadError({
                error: customerInformationResponse,
              });
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(fromActions.CustomerInformationLoadError({ error })),
          ),
        ),
      ),
    );
  });

  public navigateToStep1WhenCustomerInformationLoadComplete$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationLoadComplete),
        tap(() => this.router.navigate([RoutesPath.PERSONAL_PAGE], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public navigateToTerminationWhenPersonalInformationUpdated$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.UpdatePersonalInformation),
        tap(() =>
          this.router.navigate([RoutesPath.TERMINATION_PAGE], { skipLocationChange: true }),
        ),
      );
    },
    { dispatch: false },
  );

  public navigateToSummaryWhenFormIsSaved$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.TerminationInformationSave),
        tap(() => this.router.navigate([RoutesPath.SUMMARY_PAGE], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public loadProductList$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.ProductListLoad),
      switchMap(() =>
        this.productListService.getProductList().pipe(
          map((productListResponse: ProductListResponse) => {
            return fromActions.ProductListLoadComplete({
              groundsList: productListResponse.groundsList,
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              bank: this.jsonConvert.deserializeArray(productListResponse.banque, Product),
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              insurances: this.jsonConvert.deserializeArray(
                productListResponse.assurances,
                Product,
              ),
            });
          }),
          catchError((error: HttpErrorResponse) => of(fromActions.ProductListLoadError({ error }))),
        ),
      ),
    );
  });

  public sendEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.SendEmailLoad),
      concatLatestFrom(() =>
        this.store.select(fromStore.selectTerminationInformationForEmailRequest),
      ),
      switchMap(([, terminationRequest]) => {
        return this.emailService.sendEmail(terminationRequest).pipe(
          map(() => {
            return fromActions.SendEmailLoadComplete();
          }),
          catchError((error: HttpErrorResponse) => of(fromActions.SendEmailLoadError({ error }))),
        );
      }),
    );
  });

  public terminationComplete$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.SendEmailLoadComplete, fromActions.sendRequestActions.sendCompleted),
        tap(() =>
          this.router.navigate([RoutesPath.CONFIRMATION_PAGE], { skipLocationChange: true }),
        ),
      );
    },
    { dispatch: false },
  );

  public sendRequest$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.sendRequestActions.send),
      concatLatestFrom(() => [
        this.store.select(fromStore.selectTerminationInformation),
        this.store.select(fromStore.selectPersonalInformation),
        this.store.select(fromStore.selectInsurances),
        this.store.select(fromStore.selectBankingProducts),
      ]),
      switchMap(([, terminationInformation, personalInformation, insurances, bankingProducts]) => {
        const terminationRequest = this.requestService.payloadRequestMapper(
          personalInformation,
          terminationInformation,
          insurances,
          bankingProducts,
        );
        return this.requestService.sendRequest(terminationRequest).pipe(
          map(() => {
            return fromActions.sendRequestActions.sendCompleted();
          }),
          catchError((error: HttpErrorResponse) =>
            of(fromActions.sendRequestActions.sendFailed({ error })),
          ),
        );
      }),
    );
  });

  public redirectToErrorPageWhenApiError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          fromActions.CustomerInformationLoadError,
          fromActions.ProductListLoadError,
          fromActions.SendEmailLoadError,
          fromActions.sendRequestActions.sendFailed,
        ),
        tap(() => this.router.navigate([RoutesPath.ERROR_PAGE], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );
}
